/**
 * Where the back-office can go, in one list.
 *
 * The sidebar renders it and the breadcrumbs resolve against it, so the two cannot
 * disagree about what a section is called or where it starts. A second copy is how a
 * drawer says "Authoring" while the trail above the page says "Generation".
 *
 * Sections only. A screen reachable by id — a job, a lesson, a collection — is not a
 * destination anyone can pick from a menu, and `crumbsFor` names those from the path.
 */

export interface Section {
  readonly path: string;
  readonly label: string;
}

export const SECTIONS: readonly Section[] = [
  { path: '/', label: 'Diagnostics' },
  { path: '/action-center', label: 'Action Center' },
  { path: '/qa-audit', label: 'QA audit' },
  { path: '/library/courses', label: 'Courses' },
  { path: '/library/modules', label: 'Modules' },
  { path: '/library/paths', label: 'Paths' },
  { path: '/library/lessons', label: 'Lessons' },
  { path: '/curriculum', label: 'Curriculum' },
  { path: '/authoring', label: 'Authoring' },
  { path: '/onboarding', label: 'Onboarding' },
  { path: '/system-prompts', label: 'System prompts' },
];

/**
 * Which section a path belongs to, or nothing at the root.
 *
 * The drawer's highlight and the breadcrumb's first crumb are the same question, and
 * answering it twice is how one says "Authoring" while the other does not. The root is
 * matched exactly rather than by prefix: every path starts with `/`, so a prefix match
 * would give it every screen in the app.
 */
export function sectionFor(pathname: string): Section | undefined {
  return SECTIONS.filter((one) => one.path !== '/').find((one) =>
    pathname.startsWith(one.path),
  );
}

export interface Crumb {
  readonly label: string;
  /** Absent on the last crumb: the page you are already on is not a link. */
  readonly to?: string;
}

/**
 * What the sub-paths of a section are called.
 *
 * Keyed by the segment that follows the section, because that is what a URL carries. An
 * id-bearing screen gets a static label — "Job", not the id — since a breadcrumb is for
 * getting back rather than for identifying which row you are on, and the page heading
 * already says that.
 */
const LEAVES: Record<string, string> = {
  new: 'New run',
  ledger: 'Ledger',
  jobs: 'Job',
  lessons: 'Review',
  collections: 'Module',
  courses: 'Course',
};

/**
 * The trail for a path, resolved against the sections above.
 *
 * Deliberately derived rather than passed in by each screen. A screen that names its own
 * ancestors is a screen that can be wrong about them, and the one thing a breadcrumb has
 * to get right is where "back" goes.
 */
export function crumbsFor(pathname: string): Crumb[] {
  const section = sectionFor(pathname);

  if (section === undefined) return [];

  const rest = pathname
    .slice(section.path.length)
    .split('/')
    .filter((part) => part !== '');

  // The section itself is a link only when it is not where we already are.
  const trail: Crumb[] =
    rest.length === 0
      ? [{ label: section.label }]
      : [{ label: section.label, to: section.path }];

  const leaf = rest[0];
  if (leaf !== undefined && LEAVES[leaf] !== undefined) {
    trail.push({ label: LEAVES[leaf] });
  }

  return trail;
}
